import {Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";

import {FormGroup, FormsModule, ReactiveFormsModule, UntypedFormControl} from "@angular/forms";
import {
  faChevronRight,
  faCircleCheck,
  faCircleExclamation,
  faFile,
  faFolder,
  faFolderOpen,
  faHouse,
  faTrash,
  faTrashArrowUp,
  faTrashXmark,
  faLock,
  faPenToSquare,
  faX
} from "@fortawesome/pro-solid-svg-icons";

import {faSpinnerThird} from "@fortawesome/pro-duotone-svg-icons";

import {ProjectFile, ProjectFileControllerService, TrashEntryDTO} from "../../../../api";
import {FaIconComponent} from "@fortawesome/angular-fontawesome";
import {DatePipe, DecimalPipe, NgClass} from "@angular/common";
import {Subscription} from "rxjs";
import {
  BicButtonComponent,
  BicFileUploadProgressComponent,
  BicInputComponent,
  BicPopupComponent,
  TooltipDirective
} from "@buildinginformationcloud/bic-ui-components";
import {UploadJob, UploadService} from "../../../../services/upload/upload.service";
import {DeleteJob, DeleteService} from "../../../../services/delete/delete.service";

type CopyMoveBreadcrumbItem =
  | { type: 'folder'; depth: number; folder: ProjectFile }
  | { type: 'ellipsis'; hidden: Array<{ depth: number; folder: ProjectFile }> };
type CopyMoveBreadcrumbRenderItem = { type: 'root' } | CopyMoveBreadcrumbItem | { type: 'selected'; row: ProjectFile };

/**
 * Zeichen, die in Ordner- und Dateinamen NICHT vorkommen duerfen.
 *
 * Bewusst eine Verbotsliste und keine Erlaubnisliste. Hier stand
 * `/^[a-zA-Z0-9äöüÄÖÜß _\-]+$/`, also ohne Klammern, Punkte, Kommas, Akzente - und das war
 * strenger als noetig und obendrein widerspruechlich: bei einer Namenskollision vergibt der
 * Dateimanager SELBST Namen der Form `plan (2).pdf`. Wer so eine Datei umbenennen wollte, bekam
 * "Klammern sind nicht erlaubt" fuer einen Namen, den die Anwendung gerade erzeugt hatte
 * (Bericht 2432).
 *
 * Verboten bleibt genau das, was echten Schaden anrichtet:
 * - `< > : " / \ | ? *` sind unter Windows in Dateinamen unzulaessig. Namen landen im
 *   ZIP-Download als Pfadbestandteil - ein Archiv mit solchen Namen liesse sich nicht auspacken.
 * - `/` und `\` waeren zusaetzlich Pfadtrenner und damit ein Weg aus dem Zielverzeichnis heraus.
 * - Steuerzeichen (< 0x20) haben in einem Namen nichts verloren.
 *
 * Der Dateiname geht NICHT in den S3-Schluessel ein (der wird aus der Datei-ID gebildet), von dort
 * kommt also keine weitere Einschraenkung.
 *
 * Diese Liste darf nicht wieder zu einer engen Erlaubnisliste werden. Der Grund ist nicht
 * Bequemlichkeit: der UPLOAD validiert gar nicht, Dateinamen kommen unveraendert aus dem Dateisystem
 * des Nutzers. Waere die Regel hier strenger, liesse sich eine hochgeladene Datei nicht mehr
 * umbenennen - "Angebot!.pdf" hochladen und dann am eigenen Namen scheitern. Genau diese Falle war
 * Bericht 2432, nur mit einem anderen Zeichen. Wer enger werden will, muss den Upload mit bereinigen,
 * also die Dateien der Nutzer umbenennen; am 2026-09-15 bewusst dagegen entschieden.
 */
const FORBIDDEN_NAME_CHARS = /[<>:"/\\|?*\u0000-\u001f]/;

/** Zeichen, die im eingegebenen Namen nicht erlaubt sind - fuer eine konkrete Fehlermeldung. */
function disallowedCharsIn(name: string): string[] {
  const bad = new Set<string>();
  for (const char of name) {
    if (FORBIDDEN_NAME_CHARS.test(char)) bad.add(char);
  }
  return Array.from(bad);
}

/**
 * Prueft einen eingegebenen Namen und liefert den Grund, falls er nicht gespeichert werden kann.
 *
 * Vorher gab es nur ein boolesches `isUniqueName()`, das den Speichern-Button ausgraute: leerer
 * Name, unerlaubtes Zeichen und Namensdublette sahen identisch aus - ohne Meldung und ohne Tooltip
 * war nicht erkennbar, warum es nicht weitergeht.
 */
export function nameValidationError(
  name: string | null | undefined,
  otherNames: (string | undefined)[],
  isFolder: boolean,
): string | null {
  const trimmed = (name ?? '').trim();
  if (trimmed.length === 0) {
    return isFolder ? 'Bitte einen Ordnernamen eingeben.' : 'Bitte einen Namen eingeben.';
  }

  const bad = disallowedCharsIn(trimmed);
  if (bad.length > 0) {
    return `Nicht erlaubte Zeichen: ${bad.join(' ')} — diese Zeichen sind in Dateinamen nicht möglich.`;
  }

  // Ein Name aus lauter Punkten ist kein Name, sondern ein Pfadbestandteil ("." und "..").
  if (/^\.+$/.test(trimmed)) {
    return isFolder ? 'Bitte einen Ordnernamen eingeben.' : 'Bitte einen Namen eingeben.';
  }

  const taken = (otherNames ?? [])
    .filter((other): other is string => !!other)
    .map(other => (isFolder ? other : other.split('.')[0]).toLocaleLowerCase());
  if (taken.includes(trimmed.toLocaleLowerCase())) {
    return isFolder
      ? 'Ein Ordner mit diesem Namen existiert hier bereits.'
      : 'Eine Datei mit diesem Namen existiert hier bereits.';
  }

  return null;
}

@Component({
  selector: 'files-new-folder',
  templateUrl: './new-folder.html',
  styleUrl: './pop-ups.scss',

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [

    ReactiveFormsModule,
    BicButtonComponent,
    BicInputComponent,
    BicPopupComponent
  ]
})
export class NewFolderComponent implements OnInit {
  dialogRef = inject<MatDialogRef<NewFolderComponent>>(MatDialogRef);
  data = inject<{
    otherNames: (string | undefined)[];
  }>(MAT_DIALOG_DATA);

  folderForm!: FormGroup;

  ngOnInit(): void {
    this.folderForm = new FormGroup({
      folderName: new UntypedFormControl(""),
      folderDescription: new UntypedFormControl(""),
    });
  }

  cancelClicked(){
    this.dialogRef.close();
  }

  saveClicked(){
    this.dialogRef.close(this.folderForm.value);
  }

  /** Grund, warum nicht gespeichert werden kann - oder null. Wird im Dialog angezeigt. */
  get validationError(): string | null {
    return nameValidationError(this.folderForm?.get('folderName')?.value, this.data.otherNames, true);
  }

  /** Fehler erst zeigen, wenn ueberhaupt etwas eingegeben wurde - ein leerer Dialog ist kein Fehler. */
  get showValidationError(): boolean {
    return (this.folderForm?.get('folderName')?.value ?? '').length > 0 && this.validationError != null;
  }

  isUniqueName(){
    return this.validationError == null;
  }

  protected readonly faPenToSquare = faPenToSquare;
}


/**
 * Die obersten Elemente einer Auswahl: alles, dessen Vorfahr ebenfalls ausgewaehlt ist, faellt weg.
 *
 * Wer einen Ordner anhakt, bekommt alle Nachkommen mit in `selectedFiles` gelegt
 * (`addElementToSelectedFiles`). Fuer den Loeschdialog zaehlt aber, was der Nutzer angehakt hat:
 * ein Ordner mit drei Dateien ist "Ordner „X“ loeschen?" und nicht "4 Elemente loeschen?".
 *
 * Die Vorfahren werden ueber `allFiles` aufgeloest und nicht nur innerhalb der Auswahl, weil ein
 * Zwischenordner nicht zwingend mit ausgewaehlt ist.
 */
export function topLevelSelection(selection: ProjectFile[], allFiles: ProjectFile[]): ProjectFile[] {
  const byId = new Map<string, ProjectFile>();
  for (const pf of allFiles) {
    if (pf.id) byId.set(pf.id, pf);
  }
  const selectedIds = new Set(selection.map(pf => pf.id).filter((id): id is string => !!id));

  const hasSelectedAncestor = (pf: ProjectFile): boolean => {
    let currentId: string | undefined = pf.parentId;
    while (currentId) {
      if (selectedIds.has(currentId)) return true;
      currentId = byId.get(currentId)?.parentId;
    }
    return false;
  };

  return selection.filter(pf => !hasSelectedAncestor(pf));
}

/**
 * Titel und Text des Loeschdialogs fuer die (obersten) ausgewaehlten Elemente.
 *
 * <p>Ist Suche/Filter aktiv und steht ein Ordner im Ziel, haengt ein Hinweis an die Nachricht (#2770):
 * geloescht wird trotzdem der ganze Teilbaum ueber `originalData` (siehe `expandDeleteTargets()` in
 * `files.component.ts`), also auch Dateien, die Suche oder Filter gerade ausblenden.</p>
 */
export function deleteDialogText(files: ProjectFile[], searchOrFilterActive = false): { title: string; message: string } {
  const text = baseDeleteDialogText(files);
  if (!searchOrFilterActive || !files.some(f => f.isFolder)) {
    return text;
  }
  const hint = files.length === 1
    ? 'Der gesamte Ordnerinhalt wird mitgelöscht – auch Dateien, die durch Suche oder Filter gerade ausgeblendet sind.'
    : 'Der gesamte Inhalt der ausgewählten Ordner wird mitgelöscht – auch Dateien, die durch Suche oder Filter gerade ausgeblendet sind.';
  return {title: text.title, message: `${text.message} ${hint}`};
}

/**
 * Hinweis, dass Ordner samt ausgeblendetem Inhalt verschoben, kopiert oder gezippt werden (#2770).
 *
 * <p>Wie beim Loeschdialog: Ist Suche/Filter aktiv und steht ein Ordner unter den (obersten) Zielen,
 * nimmt die Aktion den ganzen Teilbaum mit, auch Dateien, die Suche oder Filter gerade ausblenden.
 * Singular fuer genau einen Ordner, Plural bei mehreren Elementen; sonst `null`.</p>
 */
function folderContentHint(files: ProjectFile[], searchOrFilterActive: boolean, verb: string): string | null {
  if (!searchOrFilterActive || !files.some(f => f.isFolder)) {
    return null;
  }
  return files.length === 1
    ? `Der gesamte Ordnerinhalt wird ${verb} – auch Dateien, die durch Suche oder Filter gerade ausgeblendet sind.`
    : `Der gesamte Inhalt der ausgewählten Ordner wird ${verb} – auch Dateien, die durch Suche oder Filter gerade ausgeblendet sind.`;
}

/** Hinweis im Verschieben-/Kopieren-Dialog, siehe {@link folderContentHint}. */
export function copyMoveFolderHint(files: ProjectFile[], copy: boolean, searchOrFilterActive = false): string | null {
  return folderContentHint(files, searchOrFilterActive, copy ? 'mitkopiert' : 'mitverschoben');
}

/** Hinweis im Toast des ZIP-Downloads, siehe {@link folderContentHint}. */
export function zipFolderHint(files: ProjectFile[], searchOrFilterActive = false): string | null {
  return folderContentHint(files, searchOrFilterActive, 'mit heruntergeladen');
}

function baseDeleteDialogText(files: ProjectFile[]): { title: string; message: string } {
  if (files.length > 1) {
    const noun = files.some(f => f.isFolder) ? 'Elemente' : 'Dateien';
    return {
      title: `${files.length} ${noun} löschen?`,
      message: `Möchten Sie die ${files.length} ausgewählten ${noun} wirklich löschen?`,
    };
  }
  if (files.length === 1) {
    const file = files[0];
    const article = file.isFolder ? 'den Ordner' : 'die Datei';
    return {
      title: `${file.isFolder ? 'Ordner' : 'Datei'} löschen?`,
      message: file.name
        ? `Möchten Sie ${article} „${file.name}“ wirklich löschen?`
        : `Möchten Sie ${article} wirklich löschen?`,
    };
  }
  return {
    title: 'Datei löschen?',
    message: 'Möchten Sie die Datei wirklich löschen?',
  };
}

export interface DeleteFileData {
  /** Nur die obersten Elemente der Auswahl, siehe `topLevelSelection`. */
  files: ProjectFile[];
  /** Suche oder Spaltenfilter aktiv: Hinweis, dass Ordner samt ausgeblendetem Inhalt geloescht werden (#2770). */
  searchOrFilterActive?: boolean;
}

@Component({
  selector: 'files-delete-file',
  templateUrl: './delete.html',
  styleUrl: './pop-ups.scss',

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BicButtonComponent,
    BicPopupComponent

  ]
})
export class DeleteFileComponent {
  dialogRef = inject<MatDialogRef<DeleteFileComponent>>(MatDialogRef);
  data = inject<DeleteFileData>(MAT_DIALOG_DATA);

  readonly title: string;
  readonly message: string;

  constructor() {
    ({title: this.title, message: this.message} = deleteDialogText(this.data.files, this.data.searchOrFilterActive === true));
  }

  cancelClicked(){
    this.dialogRef.close();
  }

  deleteClicked(){
    this.dialogRef.close(true);
  }
}


@Component({
  selector: 'files-rename',
  templateUrl: './rename.html',
  styleUrl: './pop-ups.scss',

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    BicButtonComponent,
    BicInputComponent,
    BicPopupComponent
  ]
})
export class RenameComponent implements OnInit {
  dialogRef = inject<MatDialogRef<RenameComponent>>(MatDialogRef);
  data = inject<{
    oldName: string;
    /** Namen der Geschwister im Zielordner. Kann Einträge ohne Namen enthalten (Upload-Dummies). */
    otherNames: (string | undefined)[];
    isFolder: boolean;
  }>(MAT_DIALOG_DATA);


  renameForm!: FormGroup;

  ngOnInit(): void {
    this.renameForm = new FormGroup({
      renameName: new UntypedFormControl(this.data.oldName),
    });
  }

  cancelClicked(){
    this.dialogRef.close();
  }

  renameClicked(){
    this.dialogRef.close(this.renameForm.value);
  }

  /** Grund, warum nicht gespeichert werden kann - oder null. Wird im Dialog angezeigt. */
  get validationError(): string | null {
    const newName = this.renameForm?.get('renameName')?.value ?? '';
    // Der unveraenderte Name ist immer zulaessig (auch wenn er sonst gegen eine Regel stiesse).
    if (newName.trim().toLocaleLowerCase() === (this.data.oldName ?? '').trim().toLocaleLowerCase()) {
      return null;
    }
    return nameValidationError(newName, this.data.otherNames, this.data.isFolder);
  }

  get showValidationError(): boolean {
    return (this.renameForm?.get('renameName')?.value ?? '').length > 0 && this.validationError != null;
  }

  isUniqueName(){
    return this.validationError == null;
  }

  protected readonly faPenToSquare = faPenToSquare;
}



@Component({
  selector: 'files-copy-move',
  templateUrl: './copy-move.html',
  styleUrl: './pop-ups.scss',

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FaIconComponent,

    NgClass,
    BicButtonComponent,
    BicPopupComponent,
    TooltipDirective
  ]
})
export class CopyMoveComponent implements OnInit {
  dialogRef = inject<MatDialogRef<CopyMoveComponent>>(MatDialogRef);
  data = inject<{
    copy: boolean;
    originalData: ProjectFile[];
    filesToMove: ProjectFile[];
    /** Suche oder Spaltenfilter aktiv: Hinweis, dass Ordner samt ausgeblendetem Inhalt mitgehen (#2770). */
    searchOrFilterActive?: boolean;
  }>(MAT_DIALOG_DATA);

  /** Hinweis auf ausgeblendeten Ordnerinhalt; gezaehlt wird, was angehakt ist (oberste Elemente). */
  readonly folderHint: string | null = copyMoveFolderHint(
    topLevelSelection(this.data.filesToMove, this.data.originalData),
    this.data.copy,
    this.data.searchOrFilterActive,
  );


  currentParent: ProjectFile | undefined = undefined;
  currentDepth: number = 0;
  parentDepthMap: Map<number, ProjectFile> = new Map<number, ProjectFile>();
  filteredData: ProjectFile[] = [];
  originalData: ProjectFile[] = [];
  selectedRow : ProjectFile | undefined = undefined;
  lastDepth : number = 0;

  ngOnInit(): void {
    this.originalData = this.data.originalData;
    this.filteredData = this.originalData.filter(file => file.parentId == null).filter(file => file.isFolder);
  }

  cancelClicked(){
    this.dialogRef.close(undefined);
  }

  approveClicked(){
    if(this.selectedRow){
      this.currentParent = this.selectedRow;
    }
    this.dialogRef.close(this.currentParent ?? '');
  }

  hasFolderChildren(row: any): boolean {
    return row.children && row.children.filter((file: { isFolder: any; }) => file.isFolder).length > 0;
  }


  navigateIntoFolder(row: any): void {
    if (row.isFolder) {
      this.selectedRow = row;
      if(row.children.filter((file: { isFolder: any; }) => file.isFolder).length > 0){
        this.filteredData = row.children.filter((file: { isFolder: any; }) => file.isFolder);
        this.currentParent = row;
        this.currentDepth++;
        this.parentDepthMap.set(this.currentDepth, row);
        this.selectedRow = undefined;
      }
    }
  }

  navigateToSpecificFolder(folder: ProjectFile | undefined, depth: number): void {
    this.currentParent = folder;
    this.currentDepth = depth;
    this.selectedRow = undefined;

    // @ts-ignore
    if(this.parentDepthMap.get(depth) && this.parentDepthMap.get(depth).children.length > 0){
      this.filteredData = this.parentDepthMap.get(depth)!.children!.filter(file => file.isFolder);
    }else{
      this.filteredData = this.originalData.filter(file => file.parentId == null).filter(file => file.isFolder);
    }
    for (let i = depth + 1; i <= this.parentDepthMap.size; i++) {
      this.parentDepthMap.delete(i);
    }
  }

  checkIfMoveDisabled(): boolean {
    if(this.selectedRow){
      // Sperren nur, wenn der Zielordner selbst einer der zu verschiebenden ORDNER ist.
      const folderIdsToMove = new Set(
        (this.data.filesToMove ?? [])
          .filter(f => f?.isFolder)
          .map(f => f.id)
          .filter((id): id is string => id != null)
      );

      if(folderIdsToMove.has(this.selectedRow.id ?? '')){
        return true;
      }
    }

    // Check if there are any files to move that are in the current folder path to prevent moving a folder into one of its subfolders
    // Das muss ebenfalls nur für ORDNER gelten: Dateien dürfen in beliebige Unterordner verschoben werden.
    const idsToMove = new Set(
      (this.data.filesToMove ?? [])
        .filter(f => f?.isFolder)
        .map(f => f.id)
        .filter((id): id is string => id != null)
    );

    for (const folder of this.parentDepthMap.values()) {
      if (folder?.id != null && idsToMove.has(folder.id)) {
        return true;
      }
    }
    return false;

  }


  protected readonly faChevronRight = faChevronRight;
  protected readonly faHouse = faHouse;
  protected readonly faFolderOpen = faFolderOpen;
  protected readonly faX = faX;
}

@Component({
  selector: 'files-new-coordination-model',
  templateUrl: './new-coordination-model.html',
  styleUrl: './pop-ups.scss',

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    BicButtonComponent,
    BicInputComponent,
    BicPopupComponent,
    FaIconComponent
  ]
})
export class NewCoordinationModelComponent implements OnInit {
  dialogRef = inject<MatDialogRef<NewCoordinationModelComponent>>(MatDialogRef);
  data = inject<{
    ifcFiles: ProjectFile[];
    otherNames: string[];
    loading?: boolean;
  }>(MAT_DIALOG_DATA);

  coordinationForm!: FormGroup;
  protected readonly faPenToSquare = faPenToSquare;
  /**
   * Verknuepfte Dateien und Namensliste kommen im Viewer-Pfad erst nach dem Oeffnen
   * (siehe BimViewerService.createCoordinationModel) -- der Dialog geht sofort auf und
   * zeigt bis dahin eine Ladeanimation, sonst wirkt der Klick wie ein Klick ins Leere.
   */
  ifcFiles: ProjectFile[] = [];
  otherNames: string[] = [];
  isLoadingFiles = false;
  loadErrorMessage: string | null = null;
  protected readonly faSpinnerThird = faSpinnerThird;

  ngOnInit(): void {
    this.coordinationForm = new FormGroup({
      name: new UntypedFormControl(''),
    });
    this.ifcFiles = this.data.ifcFiles ?? [];
    this.otherNames = this.data.otherNames ?? [];
    this.isLoadingFiles = !!this.data.loading;
  }

  cancelClicked() {
    this.dialogRef.close();
  }

  saveClicked() {
    this.dialogRef.close(this.coordinationForm.value);
  }

  /**
   * Grund, warum nicht gespeichert werden kann - oder null. Wird im Dialog angezeigt.
   *
   * Nutzt denselben Validator wie "Neuer Ordner" und "Umbenennen": vorher pruefte dieser Dialog nur
   * auf leer und Dublette, sodass hier Sonderzeichen durchgingen, die der Dateimanager an keiner
   * anderen Stelle zulaesst.
   */
  get validationError(): string | null {
    return nameValidationError(this.coordinationForm?.get('name')?.value, this.otherNames, false);
  }

  /** Fehler erst zeigen, wenn ueberhaupt etwas eingegeben wurde - ein leerer Dialog ist kein Fehler. */
  get showValidationError(): boolean {
    return (this.coordinationForm?.get('name')?.value ?? '').length > 0 && this.validationError != null;
  }

  isValidName(): boolean {
    // Ladezustand und Ladefehler sperren zusaetzlich; beide werden bereits in der Dateiliste des
    // Dialogs erklaert und gehoeren nicht noch ein zweites Mal unter das Namensfeld.
    if (this.isLoadingFiles || this.loadErrorMessage) return false;
    return this.validationError == null;
  }

  /** Nachgeladene Modelle uebernehmen und die Ladeanimation beenden. */
  setLinkedFiles(files: ProjectFile[], otherNames: string[]): void {
    this.ifcFiles = files;
    this.otherNames = otherNames;
    this.isLoadingFiles = false;
    this.loadErrorMessage = null;
  }

  /** Laden fehlgeschlagen: Grund im Dialog zeigen und "Erstellen" sperren. */
  setLoadingFailed(message: string): void {
    this.ifcFiles = [];
    this.isLoadingFiles = false;
    this.loadErrorMessage = message;
  }
}


@Component({
  selector: 'files-edit-coordination-model',
  templateUrl: './edit-coordination-model.html',
  styleUrl: './pop-ups.scss',

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BicButtonComponent,
    BicPopupComponent,
    FormsModule
  ]
})
export class EditCoordinationModelComponent implements OnInit {
  dialogRef = inject<MatDialogRef<EditCoordinationModelComponent>>(MatDialogRef);
  data = inject<{
    ifcFiles: ProjectFile[];
    linkedFileIds: string[];
  }>(MAT_DIALOG_DATA);

  selectedIds: Set<string> = new Set<string>();

  ngOnInit(): void {
    this.selectedIds = new Set(this.data.linkedFileIds);
  }

  toggleFile(id: string): void {
    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id);
    } else {
      this.selectedIds.add(id);
    }
  }

  isSelected(id: string): boolean {
    return this.selectedIds.has(id);
  }

  cancelClicked() {
    this.dialogRef.close();
  }

  saveClicked() {
    this.dialogRef.close({linkedFileIds: Array.from(this.selectedIds)});
  }
}


@Component({
  selector: 'files-select-models-for-bim-viewer',
  templateUrl: './select-models-for-bim-viewer.html',
  styleUrl: './pop-ups.scss',

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BicButtonComponent,
    BicPopupComponent,
    FaIconComponent,
    FormsModule
  ]
})
export class SelectModelsForBimViewerComponent implements OnInit {
  dialogRef = inject<MatDialogRef<SelectModelsForBimViewerComponent>>(MatDialogRef);
  data = inject<{
    ifcFiles: ProjectFile[];
    coordinationModels?: ProjectFile[];
    allowMultiple?: boolean;
    loading?: boolean;
  }>(MAT_DIALOG_DATA);

  selectedIds: Set<string> = new Set<string>();
  allowMultiple: boolean = true;
  ifcFiles: ProjectFile[] = [];
  /**
   * Gespeicherte Koordinationsmodelle. Eigene Liste statt Teil von {@link ifcFiles}: sie stehen fuer
   * mehrere Fachmodelle und werden vom Aufrufer aufgeloest (BimViewerService.resolveToIfcFiles).
   */
  coordinationModels: ProjectFile[] = [];
  isLoadingFiles = false;
  hasLoadingError = false;
  protected readonly faSpinnerThird = faSpinnerThird;

  protected get hasSelectableFiles(): boolean {
    return this.selectableFiles.length > 0;
  }

  /** Alle auswaehlbaren Einträge -- fuer Selektion und Leer-Zustand. */
  private get selectableFiles(): ProjectFile[] {
    return [...this.coordinationModels, ...this.ifcFiles];
  }

  ngOnInit(): void {
    this.allowMultiple = this.data.allowMultiple !== false;
    this.ifcFiles = this.data.ifcFiles ?? [];
    this.coordinationModels = this.data.coordinationModels ?? [];
    this.isLoadingFiles = !!this.data.loading;
  }

  selectClicked() {
    this.dialogRef.close({
      selectedFileIds: Array.from(this.selectedIds),
      selectedFiles: this.selectableFiles.filter(f => this.selectedIds.has(f.id!))
    });
  }

  toggleFile(id: string): void {
    if (!this.allowMultiple) {
      // Single selection mode
      this.selectedIds.clear();
      if (!this.selectedIds.has(id)) {
        this.selectedIds.add(id);
      }
    } else {
      // Multiple selection mode
      if (this.selectedIds.has(id)) {
        this.selectedIds.delete(id);
      } else {
        this.selectedIds.add(id);
      }
    }
  }

  isSelected(id: string): boolean {
    return this.selectedIds.has(id);
  }

  cancelClicked() {
    this.dialogRef.close();
  }

  getSelectionLabel(): string {
    if (this.isLoadingFiles) {
      return 'Modelle werden geladen…';
    }
    if (this.hasLoadingError) {
      return 'Modelle konnten nicht geladen werden';
    }
    if (this.selectedIds.size === 0) {
      return 'Keine Modelle ausgewählt';
    }
    return this.selectedIds.size === 1
      ? '1 Modell ausgewählt'
      : `${this.selectedIds.size} Modelle ausgewählt`;
  }

  setIfcFiles(files: ProjectFile[], coordinationModels: ProjectFile[] = []): void {
    this.ifcFiles = files;
    this.coordinationModels = coordinationModels;
    this.isLoadingFiles = false;
    this.hasLoadingError = false;
  }

  setLoadingFailed(): void {
    this.ifcFiles = [];
    this.coordinationModels = [];
    this.isLoadingFiles = false;
    this.hasLoadingError = true;
  }

  /** Anzahl verknuepfter Fachmodelle eines Koordinationsmodells. */
  protected linkedCount(file: ProjectFile): number {
    return (file.linkedFiles ?? []).length;
  }
}

export interface FilesRejectedData {
  unsupportedType: string[];
  tooLarge: string[];
  validCount: number;
}

export interface RejectedFileFormatGroup {
  extension: string;
  count: number;
  preview: string[];
  hiddenCount: number;
}

const REJECTED_FILE_PREVIEW_LIMIT = 5;

@Component({
  selector: 'files-rejected',
  templateUrl: './files-rejected.html',
  styleUrl: './pop-ups.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BicButtonComponent,
    BicPopupComponent
  ]
})
export class FilesRejectedComponent {
  dialogRef = inject<MatDialogRef<FilesRejectedComponent>>(MatDialogRef);
  data = inject<FilesRejectedData>(MAT_DIALOG_DATA);

  unsupportedTypeGroups: RejectedFileFormatGroup[] = this.groupByExtension(this.data.unsupportedType);
  tooLargeGroups: RejectedFileFormatGroup[] = this.groupByExtension(this.data.tooLarge);

  cancelClicked() {
    this.dialogRef.close(undefined);
  }

  // Fasst Dateien nach Endung zusammen, damit große Uploads nicht als lange Einzeldatei-Liste angezeigt werden.
  private groupByExtension(names: string[]): RejectedFileFormatGroup[] {
    const namesByExtension = new Map<string, string[]>();
    for (const name of names) {
      const extension = this.getExtension(name);
      namesByExtension.set(extension, [...(namesByExtension.get(extension) ?? []), name]);
    }
    return Array.from(namesByExtension.entries())
      .map(([extension, groupNames]) => ({
        extension,
        count: groupNames.length,
        preview: groupNames.slice(0, REJECTED_FILE_PREVIEW_LIMIT),
        hiddenCount: Math.max(0, groupNames.length - REJECTED_FILE_PREVIEW_LIMIT),
      }))
      .sort((a, b) => b.count - a.count);
  }

  private getExtension(name: string): string {
    const dotIndex = name.lastIndexOf('.');
    return dotIndex >= 0 ? name.slice(dotIndex + 1).toUpperCase() : 'Unbekannt';
  }
}

export interface StorageLimitExceededData {
  usedGb: number;
  limitGb: number;
  requiredGb: number;
  freeGb: number;
}

@Component({
  selector: 'files-storage-limit-exceeded',
  templateUrl: './storage-limit-exceeded.html',
  styleUrl: './pop-ups.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BicButtonComponent,
    BicPopupComponent,
    DecimalPipe
  ]
})
export class StorageLimitExceededComponent {
  dialogRef = inject<MatDialogRef<StorageLimitExceededComponent>>(MatDialogRef);
  data = inject<StorageLimitExceededData>(MAT_DIALOG_DATA);

  cancelClicked() {
    this.dialogRef.close(undefined);
  }
}

export interface UploadProgressData {
  /** Klammer über alle Jobs dieser Dateiauswahl. */
  batchId: string;
  /** Wie viele Dateien erwartet werden. Beim Ordner-Upload entstehen die Jobs erst nach dem Anlegen der Struktur. */
  expectedFileCount: number;
  /** Anzahl der anzulegenden Ordner (0 beim reinen Datei-Upload) \- macht den ersten Schritt sichtbar. */
  folderCount?: number;
  /**
   * Jobs, die schon vor dem Öffnen des Dialogs gesehen wurden (z. B. während der Ablehnungs-Hinweis
   * offen war). Der UploadService räumt fertige Jobs weg - ohne Startbestand hinge der Dialog bei 0/n.
   */
  initialJobs?: UploadJob[];
}

export type UploadProgressResult = 'done' | 'cancelled' | 'background';

/**
 * Zeigt den Fortschritt einer Dateiauswahl.
 *
 * Drei Wege hinaus: "Weiter" (erst wenn alles durch ist), "Im Hintergrund weiterlaufen lassen"
 * (jederzeit \- der Upload läuft im UploadService weiter, der die Verbindungen hält, und die
 * Seitenleisten-Badge zeigt den Fortschritt) und der Abbruch, der bestätigt werden muss, weil dabei
 * die noch nicht hochgeladenen Dateien verloren gehen.
 *
 * Anders als beim Löschen ist "im Hintergrund" hier NICHT reload-fest: die Bytes liegen im Browser,
 * ein Neuladen der Seite bricht ab, was noch aussteht. Der Hinweistext sagt das.
 */
@Component({
  selector: 'files-upload-progress',
  templateUrl: './upload-progress.html',
  styleUrl: './pop-ups.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BicButtonComponent,
    BicPopupComponent,
    BicFileUploadProgressComponent,
    FaIconComponent
  ]
})
export class UploadProgressComponent implements OnDestroy {
  dialogRef = inject<MatDialogRef<UploadProgressComponent, UploadProgressResult>>(MatDialogRef);
  data = inject<UploadProgressData>(MAT_DIALOG_DATA);
  jobs: UploadJob[] = [];
  /**
   * Wie viele Jobs der Upload-Pfad wirklich eingereiht hat, sobald er damit fertig ist.
   *
   * Wird von FilesComponent gesetzt, nachdem alle enqueueFiles()-Aufrufe durch sind. Vorher war
   * `expectedFileCount` das Kriterium fuer "fertig": entstanden weniger Jobs als erwartet - etwa
   * weil createFolderStructure fuer einen Pfad keine ID zurueckgab -, blieb der Dialog fuer immer
   * bei "Bitte lassen Sie dieses Fenster geöffnet…" und "Weiter" liess sich nie klicken.
   */
  enqueuedFileCount: number | null = null;
  /** Dateien, die es gar nicht in die Queue geschafft haben (Differenz zu expectedFileCount). */
  notEnqueuedCount = 0;
  /** Zeigt statt des Fortschritts die Abbruch-Rückfrage. */
  confirmCancel = false;
  /** Sekunden, die das Backend schon an der Ordnerstruktur arbeitet (nur für den Hinweistext). */
  waitingSeconds = 0;
  protected readonly faCircleCheck = faCircleCheck;
  protected readonly faFolder = faFolder;
  private readonly uploadService = inject(UploadService);
  /**
   * Eigene Kopie der Jobs: der UploadService räumt fertige Jobs auf (clearFinished),
   * die Liste im Dialog soll danach aber sichtbar bleiben.
   */
  private readonly jobsByClientId = new Map<string, UploadJob>();
  private readonly sub: Subscription;
  private readonly waitTimer: ReturnType<typeof setInterval>;

  constructor() {
    for (const job of this.data.initialJobs ?? []) {
      this.jobsByClientId.set(job.clientId, job);
    }
    this.jobs = Array.from(this.jobsByClientId.values()).sort((a, b) => a.createdAt - b.createdAt);

    this.sub = this.uploadService.jobsForBatch$(this.data.batchId).subscribe(jobs => {
      for (const job of jobs) {
        this.jobsByClientId.set(job.clientId, job);
      }
      this.jobs = Array.from(this.jobsByClientId.values()).sort((a, b) => a.createdAt - b.createdAt);
    });

    this.waitTimer = setInterval(() => {
      if (this.jobs.length === 0) {
        this.waitingSeconds++;
      }
    }, 1000);
  }

  get totalCount(): number {
    return Math.max(this.data.expectedFileCount, this.jobs.length);
  }

  get folderCount(): number {
    return this.data.folderCount ?? 0;
  }

  /** Solange keine Jobs existieren, legt das Backend noch die Ordnerstruktur an. */
  get foldersPending(): boolean {
    return this.jobs.length === 0;
  }

  /**
   * Erst wenn alle eingereihten Jobs einen Endzustand haben, darf weitergeklickt werden.
   *
   * Solange der Upload-Pfad noch einreiht (enqueuedFileCount === null), gilt die erwartete Anzahl
   * als Untergrenze - danach zaehlt nur noch, was tatsaechlich in der Queue landete.
   */
  get finished(): boolean {
    if (this.jobs.length === 0) return false;

    const target = this.enqueuedFileCount ?? this.data.expectedFileCount;
    return this.jobs.length >= target
      && this.jobs.every(job => job.state === 'done' || job.state === 'error');
  }

  /** Fehlgeschlagene Dateien (Abbrüche zählen nicht als Fehler). */
  get failedCount(): number {
    return this.jobs.filter(job => job.state === 'error' && job.error !== 'cancelled').length;
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    clearInterval(this.waitTimer);
  }

  closeClicked(): void {
    if (this.finished) {
      this.dialogRef.close('done');
      return;
    }
    this.confirmCancel = true;
  }

  /**
   * Dialog zu, Upload läuft weiter.
   *
   * Kein Abbruch und keine Rückfrage: die Jobs und ihre Subscriptions leben im UploadService
   * (providedIn: 'root'), nicht in diesem Dialog. Der Aufrufer wertet nur 'cancelled' aus, dieses
   * Ergebnis braucht dort also keine Behandlung.
   */
  runInBackground(): void {
    this.dialogRef.close('background');
  }

  keepUploading(): void {
    this.confirmCancel = false;
  }

  confirmCancelClicked(): void {
    this.uploadService.cancelBatch(this.data.batchId);
    this.dialogRef.close('cancelled');
  }

  continueClicked(): void {
    this.dialogRef.close('done');
  }
}

export interface DeleteProgressData {
  /** Klammer über alle Jobs dieser Löschaktion. */
  batchId: string;
}

/**
 * Zeigt den Fortschritt einer Löschaktion, analog zum {@link UploadProgressComponent}.
 *
 * Anders als beim Upload gibt es hier bewusst KEINEN Abbruch: die Löschung läuft serverseitig in
 * einer Transaktion, ein clientseitiges Abbrechen würde sie nicht rückgängig machen, sondern nur die
 * Rückmeldung verwerfen. Schließen ist deshalb jederzeit erlaubt - der Hinweistext sagt, dass im
 * Hintergrund weitergelöscht wird.
 */
/** Wie viele Einträge je Liste (Ordner/Dateien) standardmäßig sichtbar sind, bevor man "Alle anzeigen" braucht. */
const DELETE_PROGRESS_PREVIEW_LIMIT = 5;

@Component({
  selector: 'files-delete-progress',
  templateUrl: './delete-progress.html',
  styleUrl: './pop-ups.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BicButtonComponent,
    BicPopupComponent,
    FaIconComponent
  ]
})
export class DeleteProgressComponent implements OnDestroy {
  dialogRef = inject<MatDialogRef<DeleteProgressComponent>>(MatDialogRef);
  data = inject<DeleteProgressData>(MAT_DIALOG_DATA);
  jobs: DeleteJob[] = [];
  /** Sekunden, die die laufende Löschung schon dauert (nur für den Hinweistext). */
  waitingSeconds = 0;
  showAllFolders = false;
  showAllFiles = false;
  protected readonly previewLimit = DELETE_PROGRESS_PREVIEW_LIMIT;
  protected readonly faCircleCheck = faCircleCheck;
  protected readonly faCircleExclamation = faCircleExclamation;
  protected readonly faFolder = faFolder;
  protected readonly faFile = faFile;
  private readonly deleteService = inject(DeleteService);
  /**
   * Eigene Kopie der Jobs: der DeleteService räumt fertige Jobs auf (clearFinished),
   * die Liste im Dialog soll danach aber sichtbar bleiben.
   */
  private readonly jobsByClientId = new Map<string, DeleteJob>();
  private readonly sub: Subscription;
  private readonly waitTimer: ReturnType<typeof setInterval>;

  constructor() {
    this.sub = this.deleteService.jobsForBatch$(this.data.batchId).subscribe(jobs => {
      for (const job of jobs) {
        this.jobsByClientId.set(job.clientId, job);
      }
      this.jobs = Array.from(this.jobsByClientId.values()).sort((a, b) => a.createdAt - b.createdAt);
    });

    this.waitTimer = setInterval(() => {
      if (!this.finished) {
        this.waitingSeconds++;
      }
    }, 1000);
  }

  /** Dateien einzeln, Ordner übergeordnet - dieselbe Zweiteilung wie im Upload-Dialog. */
  get files(): DeleteJob[] {
    return this.jobs.filter(job => !job.isFolder);
  }

  get folders(): DeleteJob[] {
    return this.jobs.filter(job => job.isFolder);
  }

  get visibleFolders(): DeleteJob[] {
    return this.showAllFolders ? this.folders : this.folders.slice(0, this.previewLimit);
  }

  get visibleFiles(): DeleteJob[] {
    return this.showAllFiles ? this.files : this.files.slice(0, this.previewLimit);
  }

  /**
   * Gesamtstand ueber Ordner UND Dateien - die eine Zahl, die zur Badge in der App-Shell passt.
   *
   * Ohne sie las sich der Dialog widersprüchlich: der erste Block zeigt "8/31 Ordner", die Badge
   * daneben "280/290", und beides stimmte - der Block zaehlt eben nur Ordner, die Badge alles. Wer
   * beide gleichzeitig sieht, liest das zwangslaeufig als Fehler.
   */
  get settledCount(): number {
    return this.jobs.filter(job => job.state === 'done' || job.state === 'error').length;
  }

  get totalCount(): number {
    return this.jobs.length;
  }

  get overallProgress(): number {
    return this.percent(this.settledCount, this.totalCount);
  }

  get deletedFileCount(): number {
    return this.files.filter(job => job.state === 'done').length;
  }

  get deletedFolderCount(): number {
    return this.folders.filter(job => job.state === 'done').length;
  }

  get fileProgress(): number {
    return this.percent(this.deletedFileCount, this.files.length);
  }

  get folderProgress(): number {
    return this.percent(this.deletedFolderCount, this.folders.length);
  }

  get finished(): boolean {
    return this.jobs.length > 0 && this.jobs.every(job => job.state === 'done' || job.state === 'error');
  }

  get failedCount(): number {
    return this.jobs.filter(job => job.state === 'error').length;
  }

  /** Wie lange der Server für diese Datei gebraucht hat - macht ein langsames Löschen sichtbar. */
  durationLabel(job: DeleteJob): string {
    if (!job.finishedAt) return '';
    const seconds = Math.round((job.finishedAt - job.createdAt) / 1000);
    return seconds >= 1 ? `${seconds} s` : '';
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    clearInterval(this.waitTimer);
  }

  closeClicked(): void {
    this.dialogRef.close();
  }

  toggleShowAllFolders(): void {
    this.showAllFolders = !this.showAllFolders;
  }

  toggleShowAllFiles(): void {
    this.showAllFiles = !this.showAllFiles;
  }

  /**
   * Anteil erledigter Einträge. Bewusst über die Anzahl statt über Bytes wie beim Upload: eine Löschung
   * hat keinen übertragenen Umfang, ihre Dauer hängt an der Anzahl der Server-Prüfungen.
   */
  private percent(done: number, total: number): number {
    return total === 0 ? 0 : Math.round((100 * done) / total);
  }
}

/** Was der Papierkorb-Dialog wissen muss. */
export interface TrashData {
  projectId: number;
}

/**
 * Der Papierkorb eines Projekts.
 *
 * <p>Loeschen merkt serverseitig nur vor ({@code deletedAt}); hart geloescht wird nach Ablauf der
 * Frist von einem Aufraeumjob. Dieser Dialog ist die Sicht darauf: wiederherstellen oder die Frist
 * ueberspringen.</p>
 *
 * <p>"Endgueltig loeschen" heisst deshalb ehrlich "zur Loeschung vorgemerkt": das Backend datiert
 * das Loeschdatum zurueck, der Aufraeumjob nimmt die Zeile beim naechsten Lauf mit. Ein zweiter,
 * sofort ausfuehrender Loeschpfad waere die Rueckkehr zu den minutenlangen Wartezeiten, die der
 * Papierkorb gerade abgeschafft hat.</p>
 */
@Component({
  selector: 'files-trash',
  templateUrl: './trash.html',
  styleUrl: './pop-ups.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BicButtonComponent,
    BicPopupComponent,
    FaIconComponent,
    DatePipe,
    TooltipDirective,
  ]
})
export class TrashComponent implements OnInit {
  dialogRef = inject<MatDialogRef<TrashComponent, boolean>>(MatDialogRef);
  data = inject<TrashData>(MAT_DIALOG_DATA);

  entries: TrashEntryDTO[] = [];
  loading = true;
  message = '';
  /**
   * Zweiter Schritt beim Leeren.
   *
   * <p>Absichtlich im Dialog statt als zweites Popup: ein Dialog ueber dem Dialog ist an dieser
   * Stelle mehr Bruch als Schutz - und der Schutz ist derselbe, solange die Bestaetigung nicht an
   * der Stelle steht, an der eben noch der ausloesende Knopf war.</p>
   */
  confirmEmpty = false;
  /** Laeuft das Leeren gerade - sperrt beide Knoepfe. */
  emptying = false;
  /** Eintraege, fuer die gerade ein Request laeuft - blendet deren Knoepfe aus. */
  readonly busyIds = new Set<string>();
  readonly retentionDays = 7;
  /** Erklaerung zur Sperre - im Template zu lang, deshalb hier. */
  readonly protectedHint = 'Diese Datei wird von einer Prüfung verwendet. Würde sie entfernt, ließe '
    + 'sich der zugehörige Bericht nicht mehr anzeigen – sie bleibt deshalb erhalten und wird auch '
    + 'nach Ablauf der Frist nicht gelöscht.';

  protected readonly faFolder = faFolder;
  protected readonly faFile = faFile;
  protected readonly faTrashArrowUp = faTrashArrowUp;
  protected readonly faTrashXmark = faTrashXmark;
  protected readonly faLock = faLock;
  protected readonly faSpinnerThird = faSpinnerThird;

  private readonly fileController = inject(ProjectFileControllerService);
  /** Ob etwas geaendert wurde - der Aufrufer laedt die Dateiliste nur dann neu. */
  private changed = false;
  /** Zaehler fuer {@link refresh} - verwirft Antworten, die von einer neueren Anfrage ueberholt sind. */
  private refreshSeq = 0;

  ngOnInit(): void {
    this.load();
  }

  /**
   * Die Eintraege, die ein "Leeren" ueberhaupt treffen kann.
   *
   * <p>Pruefungs-referenzierte bleiben aussen vor, und das ist nicht nur Kosmetik: der Server lehnt
   * eine Sammel-Anfrage, in der auch nur EIN solcher Eintrag steckt, KOMPLETT ab (er merkt dann gar
   * nichts vor). Ohne diesen Filter waere "Papierkorb leeren" in jedem Projekt mit einer
   * geschuetzten Datei ein Knopf, der nichts tut.</p>
   */
  get purgeableEntries(): TrashEntryDTO[] {
    return this.entries.filter(entry => !!entry.id && !entry.usedInCheck);
  }

  /** Eintraege, die stehen bleiben - der Nutzer soll nicht raten, warum der Papierkorb nicht leer ist. */
  get protectedCount(): number {
    return this.entries.filter(entry => entry.usedInCheck).length;
  }

  askEmpty(): void {
    this.message = '';
    this.confirmEmpty = true;
  }

  cancelEmpty(): void {
    this.confirmEmpty = false;
  }

  /**
   * Merkt alle loeschbaren Eintraege auf einmal vor - ein Request, nicht einer je Zeile.
   *
   * <p>Wie beim Einzelweg heisst das Ergebnis ehrlich "vorgemerkt": hart geloescht wird vom
   * Aufraeumjob.</p>
   */
  emptyTrash(): void {
    const targets = this.purgeableEntries.map(entry => entry.id!);
    if (targets.length === 0) {
      this.confirmEmpty = false;
      return;
    }

    this.confirmEmpty = false;
    this.emptying = true;
    // Jede betroffene Zeile bekommt ihren Kreisel - dieselbe Rueckmeldung wie beim Einzelweg.
    targets.forEach(id => this.busyIds.add(id));

    this.fileController.purgeFromTrash(targets, this.data.projectId).subscribe({
      next: () => {
        this.changed = true;
        const purged = new Set(targets);
        this.entries = this.entries.filter(entry => !entry.id || !purged.has(entry.id));
        targets.forEach(id => this.busyIds.delete(id));
        this.emptying = false;
        this.refresh();

        const count = targets.length;
        this.message = (count === 1
          ? 'Ein Eintrag ist zur endgültigen Löschung vorgemerkt und verschwindet '
          : `${count} Einträge sind zur endgültigen Löschung vorgemerkt und verschwinden `)
          + 'mit dem nächsten Aufräumlauf.'
          + (this.protectedCount > 0
            ? ` ${this.protectedCount} Eintrag${this.protectedCount === 1 ? '' : 'e'} `
              + `bleib${this.protectedCount === 1 ? 't' : 'en'} erhalten, weil `
              + `${this.protectedCount === 1 ? 'er' : 'sie'} von einer Prüfung verwendet `
              + `${this.protectedCount === 1 ? 'wird' : 'werden'}.`
            : '');
      },
      error: () => {
        targets.forEach(id => this.busyIds.delete(id));
        this.emptying = false;
        this.message = 'Der Papierkorb konnte nicht geleert werden.';
      },
    });
  }

  restore(entry: TrashEntryDTO): void {
    if (!entry.id) return;
    this.busyIds.add(entry.id);
    this.fileController.restoreFromTrash([entry.id], this.data.projectId).subscribe({
      next: () => {
        this.changed = true;
        this.message = `„${entry.name}" wurde wiederhergestellt.`;
        this.afterChange(entry.id!);
      },
      error: (error: unknown) => {
        this.busyIds.delete(entry.id!);

        // 409: der Eintrag ist schon zur endgueltigen Loeschung vorgemerkt. Das kann nur ein Tab
        // mit altem Stand sein - der Server listet solche Eintraege nicht mehr auf. Statt einer
        // allgemeinen Fehlermeldung den Grund nennen und die Liste frisch holen.
        if ((error as {status?: number})?.status === 409) {
          this.message = `„${entry.name}" ist bereits zur endgültigen Löschung vorgemerkt `
            + 'und kann nicht wiederhergestellt werden.';
          this.load();
          return;
        }

        this.message = `„${entry.name}" konnte nicht wiederhergestellt werden.`;
      },
    });
  }

  purge(entry: TrashEntryDTO): void {
    if (!entry.id) return;
    this.busyIds.add(entry.id);
    this.fileController.purgeFromTrash([entry.id], this.data.projectId).subscribe({
      next: () => {
        this.changed = true;
        // Bewusst nicht "ist geloescht": das Aufraeumen laeuft im Hintergrund.
        this.message = `„${entry.name}" ist zur endgültigen Löschung vorgemerkt und verschwindet `
          + `mit dem nächsten Aufräumlauf.`;
        this.afterChange(entry.id!);
      },
      error: () => {
        this.message = `„${entry.name}" konnte nicht vorgemerkt werden.`;
        this.busyIds.delete(entry.id!);
      },
    });
  }

  closeClicked(): void {
    this.dialogRef.close(this.changed);
  }

  private afterChange(entryId: string): void {
    // Die Zeile sofort aus der Liste nehmen, statt auf das Nachladen zu warten - sonst steht sie
    // noch da, obwohl die Aktion durch ist.
    this.entries = this.entries.filter(e => e.id !== entryId);
    this.busyIds.delete(entryId);
    this.refresh();
  }

  private load(): void {
    this.loading = true;
    this.fileController.getTrash(this.data.projectId).subscribe({
      next: entries => {
        this.entries = entries;
        this.loading = false;
      },
      error: () => {
        this.entries = [];
        this.loading = false;
        this.message = 'Der Papierkorb konnte nicht geladen werden.';
      },
    });
  }

  /**
   * Holt die Liste nach einer Aktion still neu - ohne "wird geladen", die Zeilen bleiben stehen.
   *
   * <p>Eine Aktion trifft nicht nur ihre eigene Zeile: Wiederherstellen holt Vorfahren und Teilbaum
   * mit zurueck, endgueltiges Loeschen merkt den ganzen Teilbaum vor (FileTrashService.restore /
   * purgeNow). Liegt eine einzeln geloeschte Datei als eigener Eintrag neben ihrem ebenfalls
   * geloeschten Ordner (Issue 2513), verschwindet beim Wiederherstellen der Datei auch der
   * Ordner-Eintrag - und umgekehrt. Nur die angeklickte Zeile herauszunehmen liesse dann einen
   * veralteten Eintrag stehen, dessen Knoepfe auf einen laengst geaenderten Zustand zielen.</p>
   *
   * <p>Nur die Antwort der juengsten Anfrage zaehlt: eine aeltere kann einen Stand von vor der
   * letzten Aktion tragen und deren Zeile wieder einblenden.</p>
   */
  private refresh(): void {
    const request = ++this.refreshSeq;
    this.fileController.getTrash(this.data.projectId).subscribe({
      next: entries => {
        if (request === this.refreshSeq) this.entries = entries;
      },
      error: () => {
        if (request !== this.refreshSeq) return;
        this.message = (this.message ? this.message + ' ' : '')
          + 'Die Liste konnte danach nicht aktualisiert werden – bitte den Papierkorb neu öffnen.';
      },
    });
  }
}
